import { CourierListItemDto, KioskTransactionType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { MenuItem, TextField } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { createPath, Paths, ProjectPathParams } from '../../../../app/router/app.router';
import { initTable } from '../../../../ui/components/pure/list-table/list-table.init';
import { TableComponents } from '../../../../ui/components/pure/list-table/list-table-cells.component';
import { pinned } from '../../../../ui/components/pure/list-table/list-table-column-styles';
import {
  InfiniteScrollProps,
  InfiniteTable,
  PaginatedRow
} from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import {
  BadgeUserStatus,
  REJECTED_BADGE_STATUS
} from '../../../../ui/components/pure/user-status-badge/badge-user-status';
import { isCourierRejected } from '../../../couriers/logic/courier-form.map';
import { CarrierCourierStatusOptions, CarrierCouriersControlsState } from '../logic/carrier-couriers-query.map';
import { CourierCapabilitiesCell } from './courier-capabilities-cell.component';

const FIXED_COLUMN_WIDTHS = {
  capabilities: '220px',
  created: '140px',
  status: '160px'
} as const;

/** A rejected courier is left inactive, as suspended couriers are; only the badge separates them. */
const badgedStatus = (row: CourierListItemDto) =>
  isCourierRejected(row) ? REJECTED_BADGE_STATUS : (row.status as BadgeUserStatus);

/** The sortBy names the couriers endpoint accepts, narrowed to the ones this table offers. */
interface CarrierCourierSortFields {
  firstName: string;
  lastName: string;
  createdTime: string;
  status: string;
}

function useCarrierCouriersTable(inheritedCapabilities: string[]) {
  return useMemo(() => {
    const tb = initTable<CourierListItemDto, CarrierCourierSortFields>();
    // Without this, scrolling a long name into view resizes every column.
    tb.styles({ table: { tableLayout: 'fixed' } });

    // Desktop columns. `maxWidth` ellipsises the unpinned pair rather than overflowing them.
    tb.column('FIRST NAME', TableComponents.BoldText, { value: ':firstName', maxWidth: `~100%` }).options({
      isSortable: true,
      sortId: 'firstName',
      minBreakpoint: 'md',
      tooltip: 'courier first name'
    });
    tb.column('LAST NAME', TableComponents.BoldText, { value: ':lastName', maxWidth: `~100%` }).options({
      isSortable: true,
      sortId: 'lastName',
      minBreakpoint: 'md',
      tooltip: 'courier last name'
    });
    tb.column('CAPABILITIES', CourierCapabilitiesCell, {
      grants: (row: CourierListItemDto) => ({
        inherited: inheritedCapabilities as KioskTransactionType[],
        direct: row.kioskPermissions
      })
    }).options({
      isSortable: false,
      minBreakpoint: 'md',
      tooltip: 'what this courier may do',
      headerStyles: pinned(FIXED_COLUMN_WIDTHS.capabilities)
    });

    tb.column('CREATED', TableComponents.DateOnly, { value: ':createdTime' }).options({
      isSortable: true,
      sortId: 'createdTime',
      minBreakpoint: 'md',
      tooltip: 'courier created date',
      headerStyles: pinned(FIXED_COLUMN_WIDTHS.created)
    });
    tb.column('STATUS', TableComponents.UserStatus, { status: badgedStatus }).options({
      isSortable: true,
      sortId: 'status',
      minBreakpoint: 'md',
      tooltip: 'courier status',
      headerStyles: pinned(FIXED_COLUMN_WIDTHS.status)
    });

    // Mobile Columns
    tb.columnGroup('NAME')
      .row(TableComponents.BoldText, { value: ':displayName' })
      .row(TableComponents.Text, { value: ':mobile' })
      .options({ isSortable: true, sortId: 'firstName', maxBreakpoint: 'sm', tooltip: 'courier name' });
    tb.columnGroup('')
      .row(TableComponents.UserStatus, { status: badgedStatus })
      .row(TableComponents.DateOnly, { value: ':createdTime' })
      .options({
        isSortable: false,
        maxBreakpoint: 'sm',
        styles: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }
      });

    tb.title('Couriers');
    tb.rowStyles({ a: { paddingTop: '6px', paddingBottom: '6px' }, td: { height: '64px' } });

    return tb;
  }, [inheritedCapabilities]);
}

type CarrierCouriersListProps = {
  params: ProjectPathParams<Paths.carrier>;
  formState: CarrierCouriersControlsState;
  onFormInput: (update: Partial<CarrierCouriersControlsState>) => void;
  pages: PaginatedRow<CourierListItemDto>[];
  infiniteScroll: InfiniteScrollProps;
  isLoading: boolean;
  isPreviousData: boolean;
  hasError: boolean;
  /** The carrier's own capabilities, which every courier in it inherits. */
  inheritedCapabilities: string[];
};

export function CarrierCouriersList({
  params,
  pages,
  infiniteScroll,
  formState,
  onFormInput,
  isLoading,
  isPreviousData,
  hasError,
  inheritedCapabilities
}: CarrierCouriersListProps) {
  const tb = useCarrierCouriersTable(inheritedCapabilities);

  const sortData = useMemo(
    () => ({ sortBy: formState.sortBy, sortOrder: formState.sortOrder }),
    [formState.sortBy, formState.sortOrder]
  );
  const mapRowURL = useCallback(
    (row: CourierListItemDto) =>
      createPath(Paths.courier, { projectID: params.shortcode || params.projectID, courierID: row.id }),
    [params.projectID, params.shortcode]
  );

  tb.controls(CarrierCouriersListControls, { formState, onFormInput });
  tb.config({ mapRowURL });

  return (
    <InfiniteTable
      uniqueKey="id"
      pages={pages}
      infiniteScroll={infiniteScroll}
      table={tb}
      onSort={onFormInput}
      sortData={sortData}
      hasError={hasError}
      isLoading={isLoading}
      isPreviousData={isPreviousData}
      customMessage="No couriers in this carrier."
    />
  );
}

function CarrierCouriersListControls({
  formState,
  onFormInput
}: Pick<CarrierCouriersListProps, 'formState' | 'onFormInput'>) {
  return (
    <TextField
      select
      variant="outlined"
      size="small"
      value={formState.status}
      onChange={(e) => onFormInput({ status: e.target.value as CarrierCouriersControlsState['status'] })}
      slotProps={{
        select: { displayEmpty: true }
      }}
    >
      {CarrierCourierStatusOptions.map(({ label, value }) => (
        <MenuItem key={value} value={value}>
          {label}
        </MenuItem>
      ))}
    </TextField>
  );
}
